import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Sparkles, CalendarCheck, BellRing, ShieldCheck, MessageCircle, Crown,
  Loader2, ArrowRight, Check, CreditCard,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Section, Overline, Reveal } from "@/components/Primitives";
import { Seo } from "@/components/Seo";
import { TrustBadge } from "@/components/TrustBadge";
import { trackBeginCheckout } from "@/lib/analytics";

const EASE = [0.22, 0.61, 0.36, 1];
const DEPOSIT_PACK_ID = "preinscription-canada-2027";
const HERO_IMG =
  "https://images.unsplash.com/photo-1691169555002-19e569af033b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1920";

const BENEFITS = [
  { icon: Crown, title: "Place prioritaire 2027", text: "Vous êtes traité en priorité dès l'ouverture de la saison 2027." },
  { icon: CalendarCheck, title: "Consultation offerte", text: "30 minutes en visio avec un conseiller, sans engagement." },
  { icon: BellRing, title: "Alerte quotas", text: "Vous êtes prévenu(e) en premier dès l'ouverture officielle des quotas." },
  { icon: ShieldCheck, title: "Tarif garanti", text: "Le prix de votre pack est protégé contre une éventuelle hausse." },
  { icon: MessageCircle, title: "WhatsApp prioritaire", text: "Une ligne directe pour vos questions pendant la préparation." },
];

export default function Preregistration() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [payLoading, setPayLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Merci d'indiquer votre nom et votre email.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/preregistrations", { ...form, destination: "canada-2027" });
      toast.success(data.message);
      setDone(true);
    } catch (err) {
      toast.error(apiErrorMessage(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const payDeposit = async () => {
    if (!user) {
      toast.info("Créez votre compte ou connectez-vous pour régler votre acompte.");
      navigate("/connexion");
      return;
    }
    setPayLoading(true);
    try {
      const { data } = await api.post("/bookings", { pack_id: DEPOSIT_PACK_ID, origin_url: window.location.origin });
      if (data.url) {
        trackBeginCheckout({ id: DEPOSIT_PACK_ID, name: "Préinscription Canada 2027", price: 49, currency: "eur" });
        window.location.href = data.url;
      } else {
        toast.success(data.message || "Dossier créé.");
        navigate("/dashboard/suivi");
      }
    } catch (err) {
      toast.error(apiErrorMessage(err.response?.data?.detail));
    } finally {
      setPayLoading(false);
    }
  };

  return (
    <div data-testid="preregistration-page">
      <Seo
        title="Préinscription Canada 2027 — Réservez votre place | Work & Travel Expert"
        description="Les candidatures Canada 2027 sont ouvertes. Préinscrivez-vous gratuitement pour obtenir une place prioritaire, une consultation gratuite de 30 minutes et être alerté dès l'ouverture des quotas."
        keywords="préinscription canada 2027, candidatures canada 2027, PVT canada 2027, permis vacances travail canada"
        path="/preinscription-canada-2027"
      />

      {/* HERO */}
      <div className="relative overflow-hidden bg-navy">
        <img src={HERO_IMG} alt="Paysage des Rocheuses canadiennes" className="absolute inset-0 h-full w-full object-cover animate-kenburns" />
        <div className="absolute inset-0 bg-navy/78" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/70 to-navy/35" />
        <div className="pointer-events-none absolute -top-32 -right-16 h-[30rem] w-[30rem] rounded-full bg-gold/12 blur-[120px] animate-float-slow" />

        <Section className="relative z-10 py-20 sm:py-24">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full bg-gold px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.18em] text-navy">
              <Sparkles size={13} /> Candidatures ouvertes
            </span>
            <h1 className="mt-5 max-w-3xl font-heading text-4xl font-medium leading-[1.08] text-white sm:text-5xl lg:text-6xl">
              <span className="mr-3">🇨🇦</span>Préinscription <span className="text-gold">Canada 2027</span>
            </h1>
            <div className="gold-line mt-6" />
            <p className="mt-6 max-w-2xl leading-relaxed text-white/85 sm:text-lg">
              Réservez dès maintenant votre place pour un départ au Canada en 2027. La préinscription est gratuite et
              sans engagement : elle vous garantit d'être accompagné(e) en priorité dès l'ouverture officielle.
            </p>
            <div className="mt-7">
              <TrustBadge theme="dark" />
            </div>
          </Reveal>
        </Section>
      </div>

      {/* AVANTAGES */}
      <div className="bg-[#F8F9FA]">
        <Section className="py-20">
          <div className="text-center">
            <Overline>Ce que la préinscription vous apporte</Overline>
            <h2 className="mt-4 font-heading text-3xl font-medium text-navy sm:text-4xl">5 avantages immédiats</h2>
            <div className="gold-line mx-auto mt-6" />
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.title} delay={i * 0.08}>
                <div
                  data-testid={`prereg-benefit-${i}`}
                  className="h-full border border-border bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-gold hover:shadow-xl"
                >
                  <div className="flex h-12 w-12 items-center justify-center bg-navy text-gold"><b.icon size={21} /></div>
                  <h3 className="mt-5 font-heading text-xl text-navy">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Section>
      </div>

      {/* FORMULAIRE + ACOMPTE */}
      <Section className="py-20">
        <div className="grid gap-10 lg:grid-cols-2 lg:gap-14">
          {/* Préinscription gratuite */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="rounded-2xl border border-border bg-white p-8 sm:p-10"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold">Étape 1 — Gratuit</span>
            <h2 className="mt-3 font-heading text-2xl text-navy sm:text-3xl">Je me préinscris pour 2027</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
              Aucun paiement, aucun engagement. Nous vous contactons pour votre consultation gratuite de 30 minutes.
            </p>

            {done ? (
              <div data-testid="prereg-success" className="mt-8 border-l-2 border-gold bg-[#F8F9FA] px-6 py-6">
                <div className="flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/15">
                    <Check size={17} className="text-gold" />
                  </span>
                  <p className="font-heading text-xl text-navy">Préinscription confirmée</p>
                </div>
                <p className="mt-3 text-sm text-slate-700">
                  Vous avez désormais une place prioritaire pour la saison 2027. Réservez votre consultation gratuite pour
                  démarrer la préparation de votre dossier.
                </p>
                <Link
                  to="/rendez-vous"
                  data-testid="prereg-booking-link"
                  className="group mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-medium text-navy transition-colors hover:bg-gold-hover"
                >
                  <CalendarCheck size={17} /> Réserver ma consultation gratuite
                  <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="prereg-form" className="mt-8 space-y-4">
                <input
                  data-testid="prereg-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Nom et prénom" required
                  className="w-full border border-border px-4 py-3 text-sm outline-none focus:border-gold"
                />
                <input
                  data-testid="prereg-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="Adresse email" required
                  className="w-full border border-border px-4 py-3 text-sm outline-none focus:border-gold"
                />
                <input
                  data-testid="prereg-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="Téléphone / WhatsApp (facultatif)"
                  className="w-full border border-border px-4 py-3 text-sm outline-none focus:border-gold"
                />
                <textarea
                  data-testid="prereg-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Votre projet en quelques mots (facultatif)" rows={3}
                  className="w-full border border-border px-4 py-3 text-sm outline-none focus:border-gold"
                />
                <button
                  type="submit" disabled={sending} data-testid="prereg-submit"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-navy py-4 text-sm font-bold uppercase tracking-[0.08em] text-white transition-colors hover:bg-navy-light disabled:opacity-60"
                >
                  {sending ? <><Loader2 size={17} className="animate-spin" /> Envoi…</> : <>Je réserve ma place gratuitement <ArrowRight size={16} /></>}
                </button>
              </form>
            )}
          </motion.div>

          {/* Acompte */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.12, ease: EASE }}
            className="relative overflow-hidden rounded-2xl bg-navy p-8 text-white sm:p-10"
          >
            <div className="pointer-events-none absolute -top-24 -right-16 h-72 w-72 rounded-full bg-gold/12 blur-[100px]" />
            <div className="relative z-10">
              <span className="text-xs uppercase tracking-[0.2em] text-gold">Étape 2 — Optionnel</span>
              <h2 className="mt-3 font-heading text-2xl sm:text-3xl">Sécuriser ma place avec un acompte</h2>
              <div className="mt-6 flex items-end gap-2">
                <span className="font-heading text-5xl leading-none text-gold">49 €</span>
                <span className="pb-1 text-sm text-white/70">une seule fois</span>
              </div>
              <p className="mt-5 text-sm leading-relaxed text-white/85">
                Cet acompte est <strong className="text-white">intégralement déduit</strong> du pack que vous choisirez ensuite.
                Il verrouille votre place et le tarif de votre accompagnement pour 2027.
              </p>
              <ul className="mt-6 space-y-3">
                {["Place et tarif verrouillés pour 2027", "Déduit à 100 % de votre pack final", "Paiement sécurisé par Stripe"].map((x) => (
                  <li key={x} className="flex items-start gap-3 text-sm text-white/90">
                    <Check size={17} className="mt-0.5 shrink-0 text-gold" /> {x}
                  </li>
                ))}
              </ul>
              <button
                onClick={payDeposit} disabled={payLoading} data-testid="prereg-deposit-btn"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold py-4 text-sm font-bold uppercase tracking-[0.08em] text-navy transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover disabled:opacity-60"
              >
                {payLoading ? <><Loader2 size={17} className="animate-spin" /> Redirection…</> : <><CreditCard size={17} /> Réserver ma place — 49 €</>}
              </button>
              <p className="mt-4 text-center text-[11px] text-white/55">
                Vous devez être connecté(e) pour régler votre acompte. La préinscription gratuite reste disponible sans compte.
              </p>
            </div>
          </motion.div>
        </div>
      </Section>
    </div>
  );
}
